import { HTMLAttributes, ReactNode } from 'react';

/**
 * ChatHeader — conversation top bar: avatar, serif name, presence line.
 */
export interface ChatHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Avatar node (e.g. <Avatar/>). */
  avatar?: ReactNode;
  /** Person / room name (serif). */
  name: string;
  /** Descriptor after the presence dot, e.g. "your host in Lisbon". */
  subtitle?: string;
  /** Show the green online dot. Default true. */
  online?: boolean;
  /** Right-aligned action area (e.g. a sign-in button). */
  actions?: ReactNode;
  /** Small inline node after the subtitle (e.g. "· Sign in" while `actions` shows progress). Brings its own separator. */
  subtitleAction?: ReactNode;
}

export function ChatHeader({ avatar, name, subtitle, online = true, actions, subtitleAction, style, ...props }: ChatHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 20px',
        ...style,
      }}
      {...props}
    >
      {avatar ? <div style={{ flex: 'none' }}>{avatar}</div> : null}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: 19, color: 'var(--text)', lineHeight: 1.1 }}>
          {name}
        </div>
        {(subtitle || online) ? (
          <div
            style={{
              color: 'var(--text-dim)',
              fontSize: 'var(--fs-meta)',
              marginTop: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-sans)',
            }}
          >
            {online ? (
              <span style={{ width: 5, height: 5, borderRadius: '999px', background: 'rgb(var(--success))', flexShrink: 0 }} />
            ) : null}
            {subtitle ? <span>{subtitle}</span> : null}
            {subtitleAction ?? null}
          </div>
        ) : null}
      </div>
      {actions ? <div style={{ marginLeft: 'auto', flex: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>{actions}</div> : null}
    </div>
  );
}

export default ChatHeader;
